import { db } from "@/lib/firebase"
import { doc, getDoc } from "firebase/firestore"
import { SAVE_ENDPOINT, UNSAVE_ENDPOINT } from '@/config/endpoints'

/**
 * Save a flash or portfolio item to specific collections for a user.
 */
export async function saveItemToCollections(
  itemId: string, 
  userId: string, 
  isFlash: boolean, 
  selectedCollections: string[]
): Promise<void> {
  const type = isFlash ? 'flash' : 'portfolioItem'
  try {
    const response = await fetch(SAVE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        itemId,
        selectedCollections,
        userId
      })
    })

    const data = await response.json().catch(() => null)
    console.log('[saveItemToCollections] Response', response.status, data)

    if (!response.ok) {
      throw new Error(`Failed to save item to collections: ${response.status}`)
    }
  } catch (err) {
    console.error('[saveItemToCollections] Error:', err)
    throw err
  }
}

/**
 * Save a flash or portfolio item for a user.
 * @deprecated Use saveItemToCollections instead
 */
export async function saveItem(itemId: string, userId: string, isFlash: boolean): Promise<void> {
  const type = isFlash ? 'flash' : 'portfolioItem'
  try {
    const response = await fetch(SAVE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        item_id: itemId,
        user_id: userId
      })
    })

    const data = await response.json().catch(() => null)
    console.log('[saveItem] Response', response.status, data)

    if (!response.ok) {
      throw new Error(`Failed to save item: ${response.status}`)
    }
  } catch (err) {
    console.error('[saveItem] Error:', err)
    throw err
  }
}

/**
 * Unsave a flash or portfolio item for a user.
 */
export async function unsaveItem(itemId: string, userId: string, isFlash: boolean): Promise<void> {
  const type = isFlash ? 'flash' : 'portfolioItem'
  try {
    const response = await fetch(UNSAVE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        item_id: itemId,
        user_id: userId
      })
    })

    const data = await response.json().catch(() => null)
    console.log('[unsaveItem] Response', response.status, data)

    if (!response.ok) {
      throw new Error(`Failed to unsave item: ${response.status}`)
    }
  } catch (err) {
    console.error('[unsaveItem] Error:', err)
    throw err
  }
}

/**
 * Get initial save state and count for an item
 */
export async function getItemSaveState(itemId: string, userId: string | null, isFlash: boolean): Promise<{
  isSaved: boolean
  savesCount: number
}> {
  const collection = isFlash ? "flashes" : "portfolioItems"
  const itemRef = doc(db, collection, itemId)
  const itemDoc = await getDoc(itemRef)
  
  if (!itemDoc.exists()) {
    return { isSaved: false, savesCount: 0 }
  }
  
  const data = itemDoc.data()
  const timedSaves = data.timedSaves || []
  return {
    isSaved: userId ? timedSaves.some((save: any) => save.userId === userId) : false,
    savesCount: timedSaves.length
  }
}

/**
 * Get all saved items for a user
 */
export async function getUserSavedItems(userId: string): Promise<string[]> {
  const userRef = doc(db, "users", userId)
  const userDoc = await getDoc(userRef)
  
  if (!userDoc.exists()) {
    return []
  }
  
  const userData = userDoc.data()
  return userData.saves || []
} 